const SUFFIXES = ['', 'K', 'M', 'B', 'T'];
const THOUSAND = 1_000;
const BASE = 10;
const DIGITS_PER_GROUP = 3;
const DEFAULT_SIGNIFICANT_FIGURES = 3;

/**
 * Rounds a positive finite number to the given number of significant figures.
 *
 * Example: `roundToSignificantFigures(7287, 2)` returns `7300`.
 *
 * Preconditions:
 *   - `num` must be finite and strictly greater than 0.
 *     Passing `0`, a negative number, `NaN`, or `±Infinity` will yield
 *     `-Infinity` or `NaN` via `Math.log10`. Callers are responsible for
 *     validating input before calling this helper.
 *   - `significantFigures` should be a positive integer.
 */
export function roundToSignificantFigures(
  num: number,
  significantFigures: number
): number {
  const exponent = significantFigures - Math.floor(Math.log10(num)) - 1;
  if (exponent >= 0) {
    const multiplier = Math.pow(BASE, exponent);
    return Math.round(num * multiplier) / multiplier;
  }
  const divisor = Math.pow(BASE, -exponent);
  return Math.round(num / divisor) * divisor;
}

/**
 * Formats a large count into a human-readable abbreviated string
 * using significant-figure rounding (adapted from TRCPS).
 *
 * Boundary-safe: values like 999,999 correctly promote to "1 M"
 * instead of displaying "1000 K".
 */
export function formatCount(
  count: number,
  significantFigures = DEFAULT_SIGNIFICANT_FIGURES
): string {
  if (isNaN(count) || count <= 0) return '';
  if (count < THOUSAND) return count.toString();

  const rounded = roundToSignificantFigures(count, significantFigures);
  const numLength = Math.floor(Math.log10(rounded)) + 1;
  const magnitude = Math.min(
    Math.floor((numLength - 1) / DIGITS_PER_GROUP),
    SUFFIXES.length - 1
  );
  const divisor = Math.pow(BASE, magnitude * DIGITS_PER_GROUP);
  const result = rounded / divisor;
  const suffix = SUFFIXES[magnitude];

  return `${result} ${suffix}`.trim();
}
